
import Vue from "vue";
import moment from "moment";
import MonthControl from "./MonthControl.vue";
import { getDatesFromStartEnd } from "@/utils/settlementUtils";
import { db } from "@/utils/dexie";
import eventBus from "@/eventBus";
import {
  filterSettlementResult,
  OrderPathFilter,
} from "@/utils/orderPathFilter";

moment.locale("ko");

export default Vue.extend({
  components: {
    MonthControl,
  },
  props: {
    calendarValue: Object,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data: () => ({
    isLoading: false,
    month_payment_composition_amount: 0,
    month_order_count: 0,
    month_refund_amount: 0,
    month_refund_count: 0,
    weeks: [] as Array<any>,
    selectedRange: [] as Array<string>,
    resultsMap: {} as Record<string, any>,
  }),
  computed: {
    monthStartDate() {
      const base = this.calendarValue;
      return base.clone().startOf("month").format("YYYY-MM-DD");
    },
    monthEndDate() {
      const base = this.calendarValue;
      const end = base.clone().endOf("month");
      return end.isAfter(moment(), "day")
        ? moment().format("YYYY-MM-DD")
        : end.format("YYYY-MM-DD");
    },
  },
  watch: {
    calendarValue: {
      async handler() {
        await this.render();
      },
      immediate: false,
    },
    orderPathFilter: {
      handler() {
        this.render();
      },
      deep: true,
    },
  },
  mounted() {
    this.render();
  },
  methods: {
    query(
      start_date: string,
      end_date: string,
      refresh = false,
      openModal = true
    ) {
      eventBus.$emit("query", start_date, end_date, refresh, openModal);
    },
    async render() {
      this.isLoading = true;
      try {
        await this.getMonthWeeks();
        if (this.weeks.length === 0) return;

        const mallId = this.$store.state.mall_info?.mall_id;
        const shopNo = this.$store.state.mall_info?.shop_no;
        if (!mallId || !shopNo) return;

        const firstDate = this.weeks[0].days[0].date;
        const lastWeek = this.weeks[this.weeks.length - 1];
        const lastDate = lastWeek.days[lastWeek.days.length - 1].date;

        const rows = await db.results
          .where("[mall_id+shop_no+date]")
          .between(
            [mallId, shopNo, firstDate],
            [mallId, shopNo, lastDate],
            true,
            true
          )
          .toArray();

        const resultsMap: Record<string, any> = {};
        for (const row of rows) {
          resultsMap[row.date] = filterSettlementResult(
            row,
            this.orderPathFilter
          );
        }

        this.$set(this, "resultsMap", resultsMap);

        for (const week of this.weeks as any) {
          week.order_count = 0;
          week.payment_composition_amount = 0;
          week.refund_count = 0;
          week.refund_amount = 0;

          for (const d of week.days) {
            const result = this.resultsMap[d.date];
            if (result) {
              week.order_count += result.order_count;
              week.payment_composition_amount +=
                result.payment_composition_amount;
              week.refund_count += result.refund_count;
              week.refund_amount += result.refund_amount;
            }
          }
        }
        await this.getMonthTotal();
      } catch (e) {
        console.error("ResultCalendar render error:", e);
      } finally {
        this.isLoading = false;
      }
    },
    isDateDisabled(dayDate: string): boolean {
      return (
        this.selectedRange.length === 1 &&
        moment(this.selectedRange[0]).diff(moment(dayDate), "months", true) > 1
      );
    },
    getMonth(): string {
      const current = this.calendarValue
        ? moment(this.calendarValue)
        : moment();
      return current.format("M");
    },
    async queryCurrentMonth() {
      const base = this.calendarValue ? moment(this.calendarValue) : moment();
      const startOfMonth = base.clone().startOf("month").format("YYYY-MM-DD");
      const endOfMonth = base.clone().endOf("month").format("YYYY-MM-DD");
      await this.query(startOfMonth, endOfMonth, true, false);
    },
    async getMonthWeeks() {
      const weeks: Array<Array<any>> = [];
      const momentBase = this.calendarValue;
      const firstDay = momentBase.clone().startOf("month").startOf("week");
      const lastDay = momentBase.clone().endOf("month").endOf("week");

      let current = firstDay.clone();
      while (current <= lastDay) {
        const week: any = { days: [] };

        for (let i = 0; i < 7; i++) {
          const day = current.clone();
          const day_formated = day.format("YYYY-MM-DD");

          week.order_count = 0;
          week.payment_composition_amount = 0;
          week.refund_count = 0;
          week.refund_amount = 0;
          week.days.push({
            date: day_formated,
            inMonth: day.month() === momentBase.month(),
            isToday: day.isSame(moment(), "day"),
            isFuture: day.isAfter(moment(), "day"),
            isPast: day.isBefore(moment(), "day"),
          });
          current.add(1, "day");
        }
        weeks.push(week);
      }

      await this.$set(this, "weeks", weeks);
    },
    getMonthTotal() {
      let order_count = 0;
      let payment_composition_amount = 0;
      let refund_count = 0;
      let refund_amount = 0;

      const dates = getDatesFromStartEnd(
        this.calendarValue.clone().startOf("month").format("YYYY-MM-DD"),
        this.calendarValue.clone().endOf("month").format("YYYY-MM-DD")
      );

      for (const d of dates) {
        const result = this.resultsMap[d];
        if (result) {
          order_count += result.order_count;
          payment_composition_amount += result.payment_composition_amount;
          refund_count += result.refund_count;
          refund_amount += result.refund_amount;
        }
      }

      this.month_payment_composition_amount = payment_composition_amount;
      this.month_order_count = order_count;
      this.month_refund_amount = refund_amount;
      this.month_refund_count = refund_count;
    },
    selectRange(date: string, e?: MouseEvent) {
      (e?.currentTarget as HTMLElement).blur();

      if (this.selectedRange.length < 1) {
        this.selectedRange.push(date);
      } else if (this.selectedRange.length == 1) {
        if (this.selectedRange[0] === date) {
          this.selectedRange = [];
        } else {
          this.selectedRange.push(date);
        }
      } else if (this.selectedRange.length > 1) {
        this.selectedRange = [date];
      }

      if (this.selectedRange.length > 1) {
        this.selectedRange.sort();
        const start = moment(this.selectedRange[0]);
        const end = moment(this.selectedRange[1]);
        if (end.diff(start, "months", true) > 3) {
          eventBus.$emit("snackbar", {
            message: "최대 3개월까지만 조회할 수 있습니다.",
          });
          return;
        }
        this.query(this.selectedRange[0], this.selectedRange[1]);
      }
    },
    getYearWeek(date: string) {
      return moment(date).week();
    },
    hasWeekData(week: any): boolean {
      if (!week || !week.days) return false;
      return week.days.some((d: any) => !!this.resultsMap[d.date]);
    },
  },
});
